/**
 * Sanctum Socket Hook
 * Manages socket event listeners and state for sanctum dungeons
 */

import { useEffect, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';

export interface SanctumRoom {
  id: string;
  type: 'entry' | 'combat' | 'overseer_office' | 'boss' | 'treasure';
  width: number;
  height: number;
  tileX: number;
  tileY: number;
  connections: string[];
  tiles: number[][];
  collisionMap: boolean[][];
  enemies: SanctumEnemy[];
  lootContainers: SanctumLootContainer[];
  steamPipes?: SteamPipe[];
}

export interface SanctumEnemy {
  id: string;
  enemyType: string;
  tileX: number;
  tileY: number;
  hp: number;
  maxHp: number;
  damage: number;
  defense: number;
  behavior: 'stationary' | 'patrol' | 'aggressive';
  aggroRange: number;
  xpReward: number;
  berryReward: number;
  defeated: boolean;
  spriteKey: string;
}

export interface SanctumLootContainer {
  id: string;
  tileX: number;
  tileY: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  looted: boolean;
  spriteKey: string;
}

export interface SteamPipe {
  id: string;
  tileX: number;
  tileY: number;
  direction: 'up' | 'down' | 'left' | 'right';
  intensity: number; // 0.5 to 1.5 for particle spray intensity
}

export interface SanctumTerminalData {
  sanctumNumber: number;
  sanctumName: string;
  sanctumType: 'survival' | 'experiment' | 'preservation';
  entries: Array<{
    category: 'purpose' | 'intent' | 'records' | 'integrity' | 'breach';
    title: string;
    content: string;
  }>;
}

export interface CorridorData {
  id: string;
  fromRoomId: string;
  toRoomId: string;
  tiles: Array<{ tileX: number; tileY: number }>;
}

export interface DungeonLayout {
  rooms: SanctumRoom[];
  corridors: CorridorData[];
  entryRoomId: string;
  bossRoomId: string;
  overseerOfficeRoomId: string;
}

export interface SanctumState {
  instanceId: string | null;
  sanctumGrade: 'A' | 'B' | 'C' | 'D' | null;
  sanctumType: 'survival' | 'experiment' | 'preservation' | null;
  dungeonSeed: number | null;
  dungeonLayout: DungeonLayout | null;
  currentRoom: SanctumRoom | null;
  entryRoomId: string | null;
  playerPosition: { roomId: string; tileX: number; tileY: number; worldX?: number; worldY?: number } | null;
  terminalData: SanctumTerminalData | null;
  isInSanctum: boolean;
}

interface SanctumRewards {
  xp: number;
  berries: number;
  items?: string[];
}

export const useSanctumSocket = (socket: Socket | null, gameRef?: React.RefObject<Phaser.Game>) => {
  const [sanctumState, setSanctumState] = useState<SanctumState>({
    instanceId: null,
    sanctumGrade: null,
    sanctumType: null,
    dungeonSeed: null,
    dungeonLayout: null,
    currentRoom: null,
    entryRoomId: null,
    playerPosition: null,
    terminalData: null,
    isInSanctum: false,
  });

  const [error, setError] = useState<string | null>(null);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Instance created - player entered sanctum
    const handleInstanceCreated = (data: {
      instanceId: string;
      sanctumNumber: number;
      sanctumType: 'survival' | 'experiment' | 'preservation';
      sanctumGrade: 'A' | 'B' | 'C' | 'D';
      dungeonSeed: number;
      entryRoomId: string;
      spawnPosition: { roomId: string; tileX: number; tileY: number };
    }) => {
      console.log('🏛️ [useSanctumSocket] Sanctum instance created:', data);
      setSanctumState(prev => ({
        ...prev,
        instanceId: data.instanceId,
        sanctumGrade: data.sanctumGrade,
        sanctumType: data.sanctumType,
        dungeonSeed: data.dungeonSeed,
        entryRoomId: data.entryRoomId,
        playerPosition: data.spawnPosition,
        isInSanctum: true,
      }));
      setError(null);
      
      // Call OceanScene to enter sanctum
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.enterSanctum) {
          console.log('🏛️ [useSanctumSocket] Calling oceanScene.enterSanctum with metadata');
          oceanScene.enterSanctum(data.instanceId, undefined, data.sanctumType, data.sanctumGrade);
        } else {
          console.error('🏛️ [useSanctumSocket] OceanScene or enterSanctum method not found');
        }
      }
    };

    // Room data received - update current room (legacy single room handler)
    const handleRoomData = (data: { room: SanctumRoom }) => {
      console.log('🏛️ [useSanctumSocket] Sanctum room data received:', data.room.id, data.room.type);
      setSanctumState(prev => ({
        ...prev,
        currentRoom: data.room,
      }));
    };

    // Full dungeon layout received - render all rooms and corridors
    const handleDungeonLayout = (data: {
      instanceId: string;
      rooms: SanctumRoom[];
      corridors: CorridorData[];
      entryRoomId: string;
      bossRoomId: string;
      overseerOfficeRoomId: string;
    }) => {
      console.log('🏛️ [useSanctumSocket] Full dungeon layout received:', data.rooms.length, 'rooms,', data.corridors.length, 'corridors');
      
      const layout: DungeonLayout = {
        rooms: data.rooms,
        corridors: data.corridors,
        entryRoomId: data.entryRoomId,
        bossRoomId: data.bossRoomId,
        overseerOfficeRoomId: data.overseerOfficeRoomId,
      };
      
      setSanctumState(prev => ({
        ...prev,
        dungeonLayout: layout,
        currentRoom: data.rooms.find(r => r.id === data.entryRoomId) || null,
      }));
      
      // Call OceanScene to render the full dungeon
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.renderSanctumDungeon) {
          console.log('🏛️ [useSanctumSocket] Calling oceanScene.renderSanctumDungeon with type:', sanctumState.sanctumType);
          oceanScene.renderSanctumDungeon(layout, sanctumState.sanctumType);
        } else {
          console.error('🏛️ [useSanctumSocket] OceanScene or renderSanctumDungeon method not found');
        }
      }
    };

    // Enemy defeated - update rewards
    const handleEnemyDefeated = (data: {
      enemyId: string;
      rewards: SanctumRewards;
    }) => {
      console.log('Enemy defeated:', data.enemyId, 'Rewards:', data.rewards);
      
      // Update current room to mark enemy as defeated
      setSanctumState(prev => {
        if (!prev.currentRoom) return prev;
        
        return {
          ...prev,
          currentRoom: {
            ...prev.currentRoom,
            enemies: prev.currentRoom.enemies.map(enemy =>
              enemy.id === data.enemyId ? { ...enemy, defeated: true } : enemy
            ),
          },
        };
      });
    };

    // Loot container spawned - render in sanctum space and open it
    const handleLootContainerSpawned = (data: {
      containerId: string;
      x: number;
      y: number;
      pieces: number;
      xp: number;
      itemCount: number;
    }) => {
      console.log(`🏛️ Loot container spawned: ${data.containerId} at (${data.x}, ${data.y})`);
      
      // Render the loot container in sanctum space
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.sanctumRenderer) {
          // Add the loot container to the sanctum renderer
          oceanScene.sanctumRenderer.spawnLootContainer(
            data.containerId,
            data.x,
            data.y,
            'common' // Default rarity for dynamically created containers
          );
        }
        
        // Auto-open the newly created loot container
        // This is the NEW NPC-dropped container created from opening a pre-placed container
        console.log(`🏛️ Auto-opening newly created loot container: ${data.containerId}`);
        gameRef.current.events.emit('lootcontainer:open', { id: data.containerId });
        
        // Request the container data from server
        if (socket) {
          socket.emit('lootcontainer:open', { id: data.containerId });
        }
      }
    };

    // Loot received - update container state
    const handleLootReceived = (data: {
      containerId: string;
      loot: { berries: number; items: string[] };
    }) => {
      console.log('Loot received from container:', data.containerId, data.loot);
      
      // Update current room to mark container as looted
      setSanctumState(prev => {
        if (!prev.currentRoom) return prev;
        
        return {
          ...prev,
          currentRoom: {
            ...prev.currentRoom,
            lootContainers: prev.currentRoom.lootContainers.map(container =>
              container.id === data.containerId ? { ...container, looted: true } : container
            ),
          },
        };
      });
      
      // Don't open pre-placed containers here - they will be opened via sanctum:loot_container_spawned
      // which provides the new NPC-dropped container ID to open
    };

    // Room cleared - all enemies defeated
    const handleRoomCleared = (data: { roomId: string }) => {
      console.log('Room cleared:', data.roomId);
    };

    // Boss defeated - major milestone
    const handleBossDefeated = (data: {
      bossId: string;
      loot: { berries: number; xp: number; items: string[]; artifact: any };
    }) => {
      console.log('Boss defeated!', data.loot);
    };

    // Terminal data - Memory Lattice lore
    const handleTerminalData = (data: { terminalData: SanctumTerminalData }) => {
      console.log('Terminal data received:', data.terminalData);
      setSanctumState(prev => ({
        ...prev,
        terminalData: data.terminalData,
      }));
      // Emit event to open terminal UI through game instance so GameCanvas receives it
      gameRef?.current?.events.emit('sanctum:openTerminal');
    };

    // Exited sanctum - return to overworld
    const handleExited = (data?: {
      rewards?: {
        roomsCleared: number;
        enemiesKilled: number;
        containersLooted: number;
        bossDefeated: boolean;
      };
      oceanReturnPosition?: { x: number; y: number };
    }) => {
      const rewards = data?.rewards ?? {
        roomsCleared: 0,
        enemiesKilled: 0,
        containersLooted: 0,
        bossDefeated: false,
      };
      console.log('🏛️ [useSanctumSocket] Exited sanctum. Final rewards:', rewards);
      console.log('🏛️ [useSanctumSocket] Ocean return position:', data?.oceanReturnPosition);
      setSanctumState({
        instanceId: null,
        sanctumGrade: null,
        sanctumType: null,
        dungeonSeed: null,
        dungeonLayout: null,
        currentRoom: null,
        entryRoomId: null,
        playerPosition: null,
        terminalData: null,
        isInSanctum: false,
      });
      
      // Call OceanScene to exit sanctum with return position
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.exitSanctum) {
          console.log('🏛️ [useSanctumSocket] Calling oceanScene.exitSanctum with return position');
          oceanScene.exitSanctum(data?.oceanReturnPosition);
        }
      }
    };

    // Reconnected to existing instance (legacy handler)
    const handleReconnected = (data: {
      instanceId: string;
      sanctumGrade: 'A' | 'B' | 'C' | 'D';
      currentRoomId: string;
      position: { tileX: number; tileY: number };
    }) => {
      console.log('Reconnected to sanctum instance:', data.instanceId);
      setSanctumState(prev => ({
        ...prev,
        instanceId: data.instanceId,
        sanctumGrade: data.sanctumGrade,
        playerPosition: {
          roomId: data.currentRoomId,
          tileX: data.position.tileX,
          tileY: data.position.tileY,
        },
        isInSanctum: true,
      }));
      
      // Request current room data
      socket.emit('sanctum:request_room', {
        instanceId: data.instanceId,
        roomId: data.currentRoomId,
      });
    };

    // Restored to sanctum on reconnect (full dungeon data)
    const handleRestored = (data: {
      instanceId: string;
      sanctumGrade: 'A' | 'B' | 'C' | 'D';
      spawnPosition: { roomId: string; tileX: number; tileY: number; worldX: number; worldY: number };
      rooms: SanctumRoom[];
      corridors: CorridorData[];
      entryRoomId: string;
      bossRoomId: string;
      overseerOfficeRoomId: string;
    }) => {
      console.log('🏛️ [useSanctumSocket] Sanctum restored on reconnect:', data.instanceId);
      
      // Update state
      setSanctumState(prev => ({
        ...prev,
        instanceId: data.instanceId,
        sanctumGrade: data.sanctumGrade,
        playerPosition: {
          roomId: data.spawnPosition.roomId,
          tileX: data.spawnPosition.tileX,
          tileY: data.spawnPosition.tileY,
        },
        isInSanctum: true,
        dungeonLayout: {
          rooms: data.rooms,
          corridors: data.corridors,
          entryRoomId: data.entryRoomId,
          bossRoomId: data.bossRoomId,
          overseerOfficeRoomId: data.overseerOfficeRoomId,
        },
      }));
      
      // Enter sanctum mode in OceanScene
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene) {
          // Enter sanctum mode
          if (oceanScene.enterSanctum) {
            console.log('🏛️ [useSanctumSocket] Calling oceanScene.enterSanctum for restore');
            oceanScene.enterSanctum(data.instanceId);
          }
          
          // Render the dungeon
          if (oceanScene.renderSanctumDungeon) {
            console.log('🏛️ [useSanctumSocket] Calling oceanScene.renderSanctumDungeon for restore');
            oceanScene.renderSanctumDungeon({
              rooms: data.rooms,
              corridors: data.corridors,
              entryRoomId: data.entryRoomId,
              bossRoomId: data.bossRoomId,
              overseerOfficeRoomId: data.overseerOfficeRoomId,
            });
          }
        }
      }
    };

    // No active instance
    const handleNoActiveInstance = () => {
      console.log('No active sanctum instance');
      setSanctumState(prev => ({ ...prev, isInSanctum: false }));
    };

    // Boss telegraph received
    const handleBossTelegraph = (data: {
      instanceId: string;
      telegraph: {
        id: string;
        centerTileX: number;
        centerTileY: number;
        radius: number;
        duration: number;
        color: number;
        type: string;
      };
      abilityName: string;
    }) => {
      console.log('🏛️ [useSanctumSocket] Boss telegraph:', data.abilityName);
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.sanctumRenderer) {
          oceanScene.sanctumRenderer.renderTelegraph(
            data.telegraph.id,
            data.telegraph.centerTileX,
            data.telegraph.centerTileY,
            data.telegraph.radius,
            data.telegraph.color,
            data.telegraph.duration,
            data.telegraph.type
          );
        }
      }
    };

    // Boss attack landed
    const handleBossAttackLanded = (data: {
      instanceId: string;
      telegraphId: string;
      damage: number;
      centerTileX: number;
      centerTileY: number;
      radius: number;
    }) => {
      console.log('🏛️ [useSanctumSocket] Boss attack landed! Damage:', data.damage);
      if (gameRef?.current) {
        const oceanScene = gameRef.current.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.sanctumRenderer) {
          oceanScene.sanctumRenderer.removeTelegraph(data.telegraphId);
        }
      }
    };

    // Boss damaged
    const handleBossDamaged = (data: {
      instanceId: string;
      bossId: string;
      damage: number;
      newHp: number;
      maxHp: number;
      hpPercent: number;
    }) => {
      console.log(`🏛️ [useSanctumSocket] Boss ${data.bossId} took ${data.damage} damage, HP: ${data.newHp}/${data.maxHp}`);
    };

    // Player takes damage
    const handleDamageTaken = (data: {
      bossId: string;
      damage: number;
      newHp: number;
      isDead: boolean;
    }) => {
      console.log(`🏛️ [useSanctumSocket] Player took ${data.damage} damage, HP: ${data.newHp}`);
      if (data.isDead) {
        console.log('🏛️ [useSanctumSocket] Player defeated!');
      }
    };

    // Damage avoided
    const handleDamageAvoided = (data: {
      bossId: string;
      type: 'dodge' | 'block';
    }) => {
      console.log(`🏛️ [useSanctumSocket] Player ${data.type}d boss attack!`);
    };

    // Boss defeated notification
    const handleBossDefeatedNotification = (data: {
      bossId: string;
      message: string;
    }) => {
      console.log(`🏛️ [useSanctumSocket] ${data.message}`);
    };

    // Error handling
    const handleError = (data: { message: string }) => {
      console.error('🏛️ [useSanctumSocket] Sanctum error:', data.message);
      setError(data.message);
    };

    // Register all socket event listeners
    socket.on('sanctum:instance_created', handleInstanceCreated);
    socket.on('sanctum:dungeon_layout', handleDungeonLayout);
    socket.on('sanctum:room_data', handleRoomData);
    socket.on('sanctum:enemy_defeated', handleEnemyDefeated);
    socket.on('sanctum:loot_container_spawned', handleLootContainerSpawned);
    socket.on('sanctum:loot_received', handleLootReceived);
    socket.on('sanctum:room_cleared', handleRoomCleared);
    socket.on('sanctum:boss_defeated', handleBossDefeated);
    socket.on('sanctum:terminal_data', handleTerminalData);
    socket.on('sanctum:exited', handleExited);
    socket.on('sanctum:reconnected', handleReconnected);
    socket.on('sanctum:restored', handleRestored);
    socket.on('sanctum:no_active_instance', handleNoActiveInstance);
    socket.on('sanctum:boss_telegraph', handleBossTelegraph);
    socket.on('sanctum:boss_attack_landed', handleBossAttackLanded);
    socket.on('sanctum:boss_damaged', handleBossDamaged);
    socket.on('sanctum:damage_taken', handleDamageTaken);
    socket.on('sanctum:damage_avoided', handleDamageAvoided);
    socket.on('sanctum:boss_defeated_notification', handleBossDefeatedNotification);
    socket.on('sanctum:error', handleError);

    // Cleanup listeners on unmount
    return () => {
      socket.off('sanctum:instance_created', handleInstanceCreated);
      socket.off('sanctum:room_data', handleRoomData);
      socket.off('sanctum:dungeon_layout', handleDungeonLayout);
      socket.off('sanctum:enemy_defeated', handleEnemyDefeated);
      socket.off('sanctum:loot_container_spawned', handleLootContainerSpawned);
      socket.off('sanctum:loot_received', handleLootReceived);
      socket.off('sanctum:room_cleared', handleRoomCleared);
      socket.off('sanctum:boss_defeated', handleBossDefeated);
      socket.off('sanctum:terminal_data', handleTerminalData);
      socket.off('sanctum:exited', handleExited);
      socket.off('sanctum:reconnected', handleReconnected);
      socket.off('sanctum:restored', handleRestored);
      socket.off('sanctum:no_active_instance', handleNoActiveInstance);
      socket.off('sanctum:boss_telegraph', handleBossTelegraph);
      socket.off('sanctum:boss_attack_landed', handleBossAttackLanded);
      socket.off('sanctum:boss_damaged', handleBossDamaged);
      socket.off('sanctum:damage_taken', handleDamageTaken);
      socket.off('sanctum:damage_avoided', handleDamageAvoided);
      socket.off('sanctum:boss_defeated_notification', handleBossDefeatedNotification);
      socket.off('sanctum:error', handleError);
    };
  }, [socket, gameRef, sanctumState.sanctumType]);

  // Helper functions to emit events
  const requestRoom = useCallback((roomId: string) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:request_room', {
      instanceId: sanctumState.instanceId,
      roomId,
    });
  }, [socket, sanctumState.instanceId]);

  const updatePosition = useCallback((roomId: string, tileX: number, tileY: number) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:update_position', {
      instanceId: sanctumState.instanceId,
      roomId,
      tileX,
      tileY,
    });
  }, [socket, sanctumState.instanceId]);

  const defeatEnemy = useCallback((enemyId: string, rewards: SanctumRewards) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:enemy_defeated', {
      instanceId: sanctumState.instanceId,
      enemyId,
      rewards,
    });
  }, [socket, sanctumState.instanceId]);

  const lootContainer = useCallback((containerId: string) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:loot_container', {
      instanceId: sanctumState.instanceId,
      containerId,
    });
  }, [socket, sanctumState.instanceId]);

  const clearRoom = useCallback((roomId: string) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:room_cleared', {
      instanceId: sanctumState.instanceId,
      roomId,
    });
  }, [socket, sanctumState.instanceId]);

  const defeatBoss = useCallback((bossId: string) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:boss_defeated', {
      instanceId: sanctumState.instanceId,
      bossId,
    });
  }, [socket, sanctumState.instanceId]);

  const requestTerminal = useCallback((roomId: string) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:request_terminal', {
      instanceId: sanctumState.instanceId,
      roomId,
    });
  }, [socket, sanctumState.instanceId]);

  const exitSanctum = useCallback(() => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:exit', {
      instanceId: sanctumState.instanceId,
    });
  }, [socket, sanctumState.instanceId]);

  const reconnect = useCallback((playerId: string) => {
    if (!socket) return;
    socket.emit('sanctum:reconnect', { playerId });
  }, [socket]);

  const playerTakeDamage = useCallback((bossId: string, damage: number, dodged: boolean) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:player_take_damage', {
      instanceId: sanctumState.instanceId,
      bossId,
      damage,
      dodged,
    });
  }, [socket, sanctumState.instanceId]);

  const bossTakeDamage = useCallback((bossId: string, damage: number) => {
    if (!socket || !sanctumState.instanceId) return;
    socket.emit('sanctum:boss_take_damage', {
      instanceId: sanctumState.instanceId,
      bossId,
      damage,
    });
  }, [socket, sanctumState.instanceId]);

  return {
    sanctumState,
    error,
    requestRoom,
    updatePosition,
    defeatEnemy,
    lootContainer,
    clearRoom,
    defeatBoss,
    requestTerminal,
    exitSanctum,
    reconnect,
    playerTakeDamage,
    bossTakeDamage,
  };
};
